import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Trash2 } from 'lucide-react';
import { apiCall } from '../utils/auth';
import { API_ENDPOINTS } from '../config/clerkApi';
import './MyCoursesPage.css';

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [singleVideos, setSingleVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchPlaylists = async () => {
      if (!isSignedIn || !isMounted) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiCall(API_ENDPOINTS.MY_COURSES, {}, getToken);
        
        if (!isMounted) return; // Check if component is still mounted
        
        if (response.ok) {
          const data = await response.json();
          
          // Обрабатываем новую структуру ответа
          if (data.playlists && data.single_videos) {
            setPlaylists(data.playlists || []);
            setSingleVideos(data.single_videos || []);
          } else {
            // Fallback для старой структуры (если data - это массив)
            setPlaylists(Array.isArray(data) ? data : []);
            setSingleVideos([]);
          }
        } else {
          setError('Ошибка загрузки курсов');
        }
      } catch (error) {
        if (!isMounted) return; // Check if component is still mounted
        console.error('Ошибка:', error);
        setError('Не удалось загрузить курсы');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPlaylists();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [isSignedIn, getToken]); // Added getToken to dependencies

  const handlePlaylistClick = (playlist) => {
    // Navigate to playlist page (all courses are now playlists)
    navigate(`/${playlist.uuid_playlist}`);
  };

  const handleSingleVideoClick = (video) => {
    // Navigate directly to single video page
    navigate(`/video/${video.uuid_video}`);
  };

  const handleDeleteClick = (e, course, isVideo = false) => {
    e.stopPropagation();
    setDeleteConfirmation({
      id: isVideo ? course.uuid_video : course.uuid_playlist,
      title: course.title,
      isVideo: isVideo
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    
    setDeleting(true);
    try {
      const endpoint = deleteConfirmation.isVideo 
        ? API_ENDPOINTS.DELETE_SINGLE_VIDEO(deleteConfirmation.id)
        : API_ENDPOINTS.DELETE_COURSE(deleteConfirmation.id);
      
      const response = await apiCall(endpoint, { method: 'DELETE' }, getToken);
      
      if (response.ok) {
        if (deleteConfirmation.isVideo) {
          setSingleVideos(prev => prev.filter(v => v.uuid_video !== deleteConfirmation.id));
        } else {
          setPlaylists(prev => prev.filter(p => p.uuid_playlist !== deleteConfirmation.id));
        }
        console.log('✅ Content deleted');
      } else {
        setError(`Error deleting ${deleteConfirmation.isVideo ? 'video' : 'course'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError(`Failed to delete ${deleteConfirmation.isVideo ? 'video' : 'course'}`);
    } finally {
      setDeleting(false);
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const getPlaylistThumbnail = (playlist) => {
    if (playlist.playlist_thumbnail) {
      return playlist.playlist_thumbnail;
    }
    if (playlist.videos && playlist.videos.length > 0 && playlist.videos[0].thumbnail) {
      return playlist.videos[0].thumbnail;
    }
    return 'https://via.placeholder.com/320x180?text=Course+Thumbnail';
  };

  const getVideoThumbnail = (video) => {
    return video.thumbnail || 'https://via.placeholder.com/320x180?text=Video+Thumbnail';
  };

  // Функция для форматирования времени из секунд
  const formatDuration = (durationSec) => {
    if (!durationSec || durationSec === 0) return null;
    
    const hours = Math.floor(durationSec / 3600);
    const minutes = Math.floor((durationSec % 3600) / 60);
    
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return '<1m';
    }
  };

  // Получаем суммарную продолжительность плейлиста
  const getPlaylistTotalDuration = (videos) => {
    if (!videos || videos.length === 0) return 0;
    
    return videos.reduce((total, video) => {
      const duration = video.duration_sec || 0;
      return total + duration;
    }, 0);
  };

  if (loading) {
    return (
      <div className="my-courses-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p className="loading-text">Loading your courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-courses-page">
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
          <button onClick={() => window.location.reload()} className="retry-button">
            🔄 Try again
          </button>
        </div>
      </div>
    );
  }

  const totalContent = playlists.length + singleVideos.length;

  return (
    <div className="my-courses-page">
      <div className="page-header">
        <h1 className="animated-title"><span className="purple-gradient-text">My Courses</span></h1>
        <p className="page-subtitle animated-subtitle">Your learning journey continues here</p>
      </div>

      {totalContent === 0 ? (
        <div className="empty-state">
          <p>No courses found. Create your first course!</p>
        </div>
      ) : (
        <div className="courses-section">
          {/* Playlists Section */}
          {playlists.length > 0 && (
            <div className="content-section">
              <h2 className="section-title">Playlists ({playlists.length})</h2>
              <div className="courses-grid">
                {playlists.map((playlist) => (
                  <div 
                    key={playlist.uuid_playlist} 
                    className="course-card"
                    onClick={() => handlePlaylistClick(playlist)}
                  >
                    <div className="course-thumbnail">
                      <img 
                        src={getPlaylistThumbnail(playlist)} 
                        alt={playlist.title}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/320x180?text=Course+Thumbnail';
                        }}
                      />
                    </div>

                    <div className="course-info">
                      <h3 className="course-title">{playlist.title}</h3>
                      <div className="course-meta">
                        <span className="video-count">
                          {playlist.videos?.length || 0} videos
                        </span>
                        {getPlaylistTotalDuration(playlist.videos) > 0 && (
                          <>
                            <span className="meta-separator">•</span>
                            <span className="duration">
                              {formatDuration(getPlaylistTotalDuration(playlist.videos))}
                            </span>
                          </>
                        )}
                      </div>
                      <button 
                        className="delete-course-button"
                        onClick={(e) => handleDeleteClick(e, playlist, false)}
                        title="Delete course"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Single Videos Section */}
          {singleVideos.length > 0 && (
            <div className="content-section">
              <h2 className="section-title">Single Videos ({singleVideos.length})</h2>
              <div className="courses-grid">
                {singleVideos.map((video) => (
                  <div 
                    key={video.uuid_video} 
                    className="course-card"
                    onClick={() => handleSingleVideoClick(video)}
                  >
                    <div className="course-thumbnail">
                      <img 
                        src={getVideoThumbnail(video)} 
                        alt={video.title}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/320x180?text=Video+Thumbnail';
                        }}
                      />
                    </div>

                    <div className="course-info">
                      <h3 className="course-title">{video.title}</h3>
                      <div className="course-meta">
                        <span className="video-count">Single Video</span>
                        {formatDuration(video.duration_sec) && (
                          <>
                            <span className="meta-separator">•</span>
                            <span className="duration">
                              {formatDuration(video.duration_sec)}
                            </span>
                          </>
                        )}
                      </div>
                      <button 
                        className="delete-course-button"
                        onClick={(e) => handleDeleteClick(e, video, true)}
                        title="Delete video"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete {deleteConfirmation.isVideo ? 'Video' : 'Course'}</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this {deleteConfirmation.isVideo ? 'video' : 'course'}?</p>
              <p className="course-title-preview">"{deleteConfirmation.title}"</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={cancelDelete} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCoursesPage; 